import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { CreditCard, Wallet, TrendingUp } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import {
  StyledCardContainer,
  StyledCreditCard,
  StyledCardContent,
  StyledCardHeader,
  StyledCardTitle,
  StyledCardType,
  StyledCardNumber,
  StyledCardFooter,
  StyledCardDetails,
  StyledStatsGrid,
  StyledStatCard,
  StyledStatHeader,
  StyledStatIcon,
  StyledStatLabel,
  StyledStatValue,
  StyledProgressBar,
  StyledProgress,
  StyledUtilizationText,
} from './EnhancedCreditCard.styles';

export interface CreditCardData {
  id: string;
  name: string;
  type: 'platinum' | 'gold' | 'black';
  number: string;
  balance: number;
  limit: number;
  expiry: string;
}

interface CreditCardProps {
  cards: CreditCardData[];
  showNumbers?: boolean;
}

const maskCardNumber = (number: string): string => {
  const cleaned = number.replace(/\s/g, '');

  return cleaned.replace(/(\d{4})(\d{4})(\d{4})(\d{4})/, '•••• •••• •••• $4');
};

const getCardGradient = (type: string): string => {
  const gradients = {
    platinum: 'linear-gradient(135deg, #e6e6fa 0%, #c4c4c4 50%, #a8a8a8 100%)',
    gold: 'linear-gradient(135deg, #ffd700 0%, #ffb347 50%, #ff8c00 100%)',
    black: 'linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 50%, #000000 100%)',
  };

  return gradients[type as keyof typeof gradients] || gradients.platinum;
};

const getUtilization = (balance: number, limit: number) => {
  const percentage = (balance / limit) * 100;
  const remaining = limit - balance;

  return {
    percentage: Math.min(percentage, 100),
    amount: remaining,
    status: percentage > 80 ? 'high' : percentage > 60 ? 'medium' : 'low',
  };
};

const getStatusColor = (status: string): string => {
  const colors = {
    high: '#ef4444',
    medium: '#f59e0b',
    low: '#10b981',
  };

  return colors[status as keyof typeof colors] || colors.low;
};

export const EnhancedCreditCard: React.FC<CreditCardProps> = ({ cards, showNumbers = true }) => {
  const [selectedCard, setSelectedCard] = useState(cards[0]);
  const ref = useRef(null);
  const isInView = useInView(ref);
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: 'easeOut' },
      });
    }
  }, [isInView, controls]);

  if (!cards || cards.length === 0) {
    return null;
  }

  const utilization = getUtilization(selectedCard.balance, selectedCard.limit);

  return (
    <StyledCardContainer ref={ref} as={motion.div} initial={{ opacity: 0, y: 50 }} animate={controls}>
      <StyledCreditCard
        $gradient={getCardGradient(selectedCard.type)}
        $isSelected={true}
        whileHover={{
          scale: 1.02,
          rotateY: 5,
          transition: { duration: 0.3 },
        }}
        onClick={() => setSelectedCard(selectedCard)}
      >
        <StyledCardContent>
          <StyledCardHeader>
            <StyledCardTitle>{selectedCard.name}</StyledCardTitle>
            <StyledCardType>{selectedCard.type.toUpperCase()}</StyledCardType>
          </StyledCardHeader>

          <StyledCardNumber>{showNumbers ? selectedCard.number : maskCardNumber(selectedCard.number)}</StyledCardNumber>

          <StyledCardFooter>
            <StyledCardDetails>
              <div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>EXPIRES</div>
                <div>{selectedCard.expiry}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>CVV</div>
                <div>{showNumbers ? '123' : '•••'}</div>
              </div>
            </StyledCardDetails>
          </StyledCardFooter>
        </StyledCardContent>
      </StyledCreditCard>

      <StyledStatsGrid>
        <StyledStatCard>
          <StyledStatHeader>
            <StyledStatIcon $color='#6366f1'>
              <Wallet size={20} />
            </StyledStatIcon>
            <StyledStatLabel>Current Balance</StyledStatLabel>
          </StyledStatHeader>
          <StyledStatValue>{showNumbers ? formatCurrency(selectedCard.balance) : '••••••'}</StyledStatValue>
        </StyledStatCard>

        <StyledStatCard>
          <StyledStatHeader>
            <StyledStatIcon $color='#10b981'>
              <CreditCard size={20} />
            </StyledStatIcon>
            <StyledStatLabel>Credit Limit</StyledStatLabel>
          </StyledStatHeader>
          <StyledStatValue>{showNumbers ? formatCurrency(selectedCard.limit) : '••••••'}</StyledStatValue>
        </StyledStatCard>

        <StyledStatCard>
          <StyledStatHeader>
            <StyledStatIcon $color='#f59e0b'>
              <TrendingUp size={20} />
            </StyledStatIcon>
            <StyledStatLabel>Available Credit</StyledStatLabel>
          </StyledStatHeader>
          <StyledStatValue>{showNumbers ? formatCurrency(utilization.amount) : '••••••'}</StyledStatValue>
        </StyledStatCard>
      </StyledStatsGrid>

      <div style={{ marginTop: '20px' }}>
        <StyledUtilizationText>
          Credit Utilization: {showNumbers ? `${utilization.percentage.toFixed(1)}%` : '••••'}
        </StyledUtilizationText>
        <StyledProgressBar>
          <StyledProgress
            $color={getStatusColor(utilization.status)}
            initial={{ width: 0 }}
            animate={{ width: `${utilization.percentage}%` }}
            transition={{ duration: 1, delay: 0.5 }}
          />
        </StyledProgressBar>
      </div>

      {cards.length > 1 && (
        <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {cards.map((card) => (
            <button
              key={card.id}
              onClick={() => setSelectedCard(card)}
              style={{
                padding: '8px 16px',
                border: `2px solid ${selectedCard.id === card.id ? '#6366f1' : '#e5e7eb'}`,
                borderRadius: '8px',
                background: selectedCard.id === card.id ? '#6366f1' : 'transparent',
                color: selectedCard.id === card.id ? 'white' : 'inherit',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s ease',
              }}
            >
              {card.name}
            </button>
          ))}
        </div>
      )}
    </StyledCardContainer>
  );
};

export default EnhancedCreditCard;
